import { colors } from '@kmns-internal/furikake/tokens'
import { LocationOn } from '@mui/icons-material'
import {
  Autocomplete,
  Checkbox,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  type Employee,
  type GetEmployeesRequest,
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { useFormik } from 'formik'
import type React from 'react'
import { useId } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import { UUID } from 'src/exShared/util/uuid'
import Yup from 'src/features/validation/yup'
import {
  addEmployee as addEmployeeAction,
  changeIsShowEditEmployeeDrawer as changeIsShowEditEmployeeDrawerAction,
  getEmployees as getEmployeesAction,
  updateEmployee as updateEmployeeAction,
} from 'src/state/ducks/employees/actions'
import type { GetEmployeesFilter } from 'src/state/ducks/employees/types'
import type { RootState } from 'src/state/store'
import FooterButton from 'src/views/components/drawer/FooterButton'

const validationSchema = Yup.object().shape({
  name: Yup.string().required().label('従業員名'),
  employeeCode: Yup.string().required().label('従業員管理番号'),
})

interface EmployeeForm {
  id?: number
  uuid?: string
  name: string
  nameKana?: string
  employeeCode: string
  placeNodeIds: string[]
}

const EditEmployee: React.FC = () => {
  const { activeEmployee, isLoading, request, filter } = useSelector(
    ({ employeesState: { employees } }: RootState) => {
      const {
        activeEmployeeUUID,
        isLoading,
        request,
        filter,
        employees: data,
      } = employees
      return {
        activeEmployee: activeEmployeeUUID
          ? data.find(({ uuid }) => uuid === activeEmployeeUUID)
          : undefined,
        isLoading,
        request,
        filter,
      }
    },
  )

  const placeNodes = useSelector(
    ({ placesState: { places, placeGroups } }: RootState) =>
      getUniqueFlattenNodes([
        ...flattenNodes(placeGroups.nodes ?? []),
        ...(places.places ?? []),
      ]),
  )

  const dispatch = useDispatch()
  const updateEmployee = (
    employee: Employee,
    isEdit: boolean,
    request: GetEmployeesRequest,
    filter?: GetEmployeesFilter,
  ) => {
    if (!isEdit) {
      dispatch(addEmployeeAction(employee))
    } else {
      if (employee.uuid !== undefined) {
        dispatch(updateEmployeeAction(employee.uuid, employee))
      }
    }
    dispatch(getEmployeesAction(request, filter))
  }
  const changeIsShowEditEmployeeDrawer = () =>
    dispatch(changeIsShowEditEmployeeDrawerAction(false))

  const { handleChange, handleSubmit, values, setFieldValue, errors, touched } =
    useFormik<EmployeeForm>({
      initialValues: {
        id: activeEmployee?.id,
        uuid: activeEmployee?.uuid ?? UUID(),
        name: activeEmployee?.name ?? '',
        nameKana: activeEmployee?.nameKana ?? '',
        employeeCode: activeEmployee?.employeeCode ?? '',
        placeNodeIds: activeEmployee?.placeNodes?.map(({ uuid }) => uuid) ?? [],
      },
      validationSchema: validationSchema,
      onSubmit: (form: EmployeeForm) =>
        updateEmployee(
          {
            ...form,
            name: form.name,
            nameKana: form.nameKana,
            employeeCode: form.employeeCode,
            placeNodes:
              form.placeNodeIds
                .map(strId => placeNodes.find(({ uuid }) => uuid === strId))
                .filter((v): v is PlaceNode => !!v) ?? [],
          },
          activeEmployee?.uuid === form.uuid,
          request,
          filter,
        ),
    })

  const renderInput = (
    key: keyof Omit<EmployeeForm, 'placeNodeIds'>,
    label: string,
  ) => (
    <Stack>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1 }}
        component="label"
        htmlFor={`employee-${key}`}
      >
        {label}
      </Typography>
      <TextField
        id={`employee-${key}`}
        fullWidth
        size="small"
        onChange={handleChange(key)}
        value={values[key]}
      />
      <FormikErrorMessage name={key} errors={errors} touched={touched} />
    </Stack>
  )

  const getPlaceNodeLabel = (option: PlaceNode) =>
    option.type === PlaceNodeTypeEnum.PlaceGroup
      ? option.placeGroup?.name || ''
      : option.place?.name || ''

  const PlaceNodeIcon = ({ type }: { type: PlaceNodeTypeEnum }) =>
    type === PlaceNodeTypeEnum.PlaceGroup ? (
      <PlaceGroupSVG style={{ width: '16px' }} color={colors.action.active} />
    ) : (
      <LocationOn sx={{ width: '16px', color: colors.action.active }} />
    )
  const employeeEditPlaceNode = useId()

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" sx={{ my: 2, px: 3 }}>
        {activeEmployee ? '従業員の編集' : '従業員の追加'}
      </Typography>
      <Divider />
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        {renderInput('name', '従業員名')}
        {renderInput('nameKana', '従業員名（フリガナ）')}
        {renderInput('employeeCode', '従業員管理番号')}
        <Divider />
        <Typography
          variant="subtitle2"
          sx={{ mb: 1 }}
          component="label"
          htmlFor={employeeEditPlaceNode}
        >
          所属する現場
        </Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          id={employeeEditPlaceNode}
          disableCloseOnSelect
          options={placeNodes}
          isOptionEqualToValue={(option, value) =>
            option.uuid === value.uuid && option.type === value.type
          }
          getOptionLabel={getPlaceNodeLabel}
          defaultValue={placeNodes?.filter(node =>
            values.placeNodeIds.includes(node.uuid),
          )}
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={option.uuid}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox size="small" checked={selected} />
                  <PlaceNodeIcon type={option.type} />
                  <span>{getPlaceNodeLabel(option)}</span>
                </Stack>
              </li>
            )
          }}
          renderValue={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index })
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size="small"
                  label={getPlaceNodeLabel(option)}
                  icon={<PlaceNodeIcon type={option.type} />}
                  sx={{ pl: 0.5 }}
                />
              )
            })
          }
          onChange={(_, newValue) => {
            setFieldValue(
              'placeNodeIds',
              newValue.map(node => node.uuid),
            )
          }}
          renderInput={params => <TextField {...params} size="small" />}
        />
      </Stack>
      <Stack sx={{ px: 3, py: 2 }}>
        <FooterButton
          spinning={isLoading}
          onCancel={changeIsShowEditEmployeeDrawer}
          onSubmit={() => handleSubmit()}
        />
      </Stack>
    </Stack>
  )
}

export default EditEmployee
